import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { api } from "../lib/api";
import { gbp } from "../lib/format";
import { Modal, PrimaryButton, QuietButton } from "./UI";
import { useMaterialUnits } from "../hooks/useMaterialUnits";
import { fmtValue } from "./RefreshFromLibraryDialog";

/* LT-20 follow-up — "Refresh all from library": every recipe line on the
 * tender compared with the Materials library in one pass; old → new per
 * field, rate before → after per line; applied in one confirmed write. */
function LineChanges({ pv, unitLabelFor }) {
  const moves = pv.material_rate_before != null && pv.material_rate_after != null
    && Math.abs(Number(pv.material_rate_before) - Number(pv.material_rate_after)) >= 0.005;
  return (
    <div className="rounded-md border border-line" data-testid={`bulk-refresh-line-${pv.line_id}`}>
      <div className="flex items-center justify-between px-3 py-1.5 bg-surface-2/60 border-b border-line-soft">
        <span className="text-ink" style={{ fontSize: 12, fontWeight: 600 }}>
          {pv.line_ref ? `${pv.line_ref} · ` : ""}{pv.description || "(line)"}
          <span className="text-ink-4 font-normal"> · {pv.phase_name}</span>
        </span>
        <span className="font-mono tnum text-ink-2" style={{ fontSize: 11 }} data-testid={`bulk-refresh-rate-${pv.line_id}`}>
          {gbp(pv.material_rate_before || 0)} → <span style={{ fontWeight: 600 }}>{gbp(pv.material_rate_after || 0)}</span>
          {!moves && <span className="text-ink-4"> (rate unchanged)</span>}
          {pv.material_rate_override && <span className="text-ink-4"> · manual override kept</span>}
        </span>
      </div>
      <table className="w-full" style={{ fontSize: 11.5 }}>
        <tbody>
          {pv.entries.filter((e) => e.changes.length).map((e) => e.changes.map((c, i) => (
            <tr key={`${e.entry_id}-${c.field}`} className="border-t border-line-soft" data-testid={`bulk-refresh-change-${e.entry_id}-${c.field}`}>
              <td className="px-3 py-1 text-ink-2" style={{ width: "30%" }}>{i === 0 ? e.description : ""}</td>
              <td className="px-2 py-1 text-ink-2" style={{ width: "28%" }}>{c.label}</td>
              <td className="px-2 py-1 text-right font-mono tnum text-ink-3 line-through">{fmtValue(c.field, c.old, unitLabelFor)}</td>
              <td className="px-1 py-1 text-center text-ink-4">→</td>
              <td className="px-3 py-1 font-mono tnum text-ink" style={{ fontWeight: 600 }}>{fmtValue(c.field, c.new, unitLabelFor)}</td>
            </tr>
          )))}
        </tbody>
      </table>
    </div>
  );
}

export function BulkRefreshDialog({ open, tenderId, onClose, onApplied }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [showCurrent, setShowCurrent] = useState(false);
  const { labelFor: unitLabelFor } = useMaterialUnits();

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setPlan(null); setError(null); setLoading(true); setShowCurrent(false);
    api.get(`/tenders/${tenderId}/refresh-preview`)
      .then((r) => { if (alive) setPlan(r.data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || "Could not compare with the library"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, tenderId]);

  const apply = async () => {
    setApplying(true);
    try {
      const r = await api.post(`/tenders/${tenderId}/refresh`);
      toast.success(`Refreshed ${r.data.changed.length} of ${r.data.lines_checked} line(s) from the library`);
      onApplied?.(r.data.tender);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setApplying(false);
    }
  };

  const changed = plan ? plan.lines.filter((l) => l.has_changes) : [];
  const current = plan ? plan.lines.filter((l) => !l.has_changes) : [];
  const CurrentChevron = showCurrent ? ChevronDown : ChevronRight;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Refresh every line from the Materials library?"
      testid="bulk-refresh-modal"
      maxWidth={760}
      footer={
        <>
          <QuietButton onClick={onClose} data-testid="bulk-refresh-cancel">Cancel</QuietButton>
          <PrimaryButton
            onClick={apply}
            disabled={applying || loading || !plan || !plan.has_changes}
            icon={applying ? Loader2 : RefreshCw}
            data-testid="bulk-refresh-confirm"
          >
            {plan && !plan.has_changes ? "Nothing to refresh" : `Apply refresh${changed.length ? ` (${changed.length} line${changed.length === 1 ? "" : "s"})` : ""}`}
          </PrimaryButton>
        </>
      }
    >
      <p className="text-ink-2 mb-3" style={{ fontSize: 12.5 }}>
        Every recipe line's frozen quantity inputs (wastage, coverage, divisibility, stock length) are compared with the
        library now. Nothing changes until you confirm; manual material-rate overrides keep their cell.
      </p>
      {loading && <p className="text-ink-3" style={{ fontSize: 12 }}>Comparing lines…</p>}
      {error && <p className="text-note-red" style={{ fontSize: 12 }} data-testid="bulk-refresh-error">{error}</p>}
      {plan && (
        <div className="space-y-3" data-testid="bulk-refresh-plan">
          <div className="text-ink-2" style={{ fontSize: 12 }} data-testid="bulk-refresh-summary">
            <span style={{ fontWeight: 600 }}>{plan.lines_with_changes}</span> of {plan.lines_checked} recipe line{plan.lines_checked === 1 ? "" : "s"} would change
            {plan.lines_without_recipe > 0 ? ` · ${plan.lines_without_recipe} ad-hoc line${plan.lines_without_recipe === 1 ? "" : "s"} without a recipe skipped` : ""}.
          </div>
          {changed.map((pv) => <LineChanges key={pv.line_id} pv={pv} unitLabelFor={unitLabelFor} />)}
          {current.length > 0 && (
            <div className="rounded-md border border-line-soft bg-surface-2/40" data-testid="bulk-refresh-current">
              <button type="button" onClick={() => setShowCurrent((v) => !v)} className="w-full flex items-center gap-1.5 px-3 py-1.5 text-left text-ink-3" style={{ fontSize: 11.5 }} data-testid="bulk-refresh-current-toggle">
                <CurrentChevron size={12} /> {current.length} line{current.length === 1 ? "" : "s"} already current
              </button>
              {showCurrent && (
                <ul className="px-3 pb-2 space-y-0.5">
                  {current.map((pv) => (
                    <li key={pv.line_id} className="text-ink-3" style={{ fontSize: 11 }} data-testid={`bulk-refresh-current-${pv.line_id}`}>
                      {pv.line_ref ? `${pv.line_ref} · ` : ""}{pv.description} <span className="text-ink-4">· {pv.phase_name}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
